// src/rms/pages/Messages.jsx
// ============================================================================
// Conversations with customers
// ============================================================================
// An inbox rather than a send form, because the value is the record: when a
// customer says "nobody told me the deposit was non-refundable", the answer is
// a thread with a date on it.
//
// Mail leaves from reservezoo.com with the operator's name on it, and replies
// come back into the thread. Nothing here touches the mail provider directly —
// see data/messageService.js.
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import { isLocalBackend } from "../data";
import {
  listMessages,
  markThreadRead,
  sendMessage,
} from "../data/messageService";
import { PageHeader, Banner, Field, TextInput, Select, EmptyState, StatusPill, formatDateTime } from "../components/ui";
import adEngine from "../ads/adEngine";

const DELIVERY_TONE = {
  delivered: "completed",
  sent: "active",
  queued: "pending",
  bounced: "overdue",
  complained: "overdue",
  failed: "cancelled",
};

function Messages() {
  const { operator, t, tLower, invalidate } = useRms();
  const { rows: threads } = useCollection("messageThreads", {}, {
    orderBy: "last_message_at",
    direction: "desc",
  });
  const { rows: customers } = useCollection("customers");

  const [activeId, setActiveId] = useState(null);
  const [composing, setComposing] = useState(false);

  const customersById = useMemo(
    () => customers.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}),
    [customers]
  );

  const active = threads.find((x) => x.id === activeId) || null;
  const unreadTotal = threads.reduce((sum, x) => sum + (x.unread_count || 0), 0);

  // Customers with no email cannot be written to at all, so they are kept out
  // of the compose picker rather than offered and then refused on send.
  const reachable = customers.filter((c) => c.email);

  return (
    <>
      <PageHeader
        title={t("messages.messages", "Messages")}
        subtitle={
          unreadTotal
            ? `${unreadTotal} unread`
            : t("messages.subtitle", "{count, plural, one {# conversation} other {# conversations}}", {
          count: threads.length,
        })
        }
        actions={
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={() => { setComposing(true); setActiveId(null); }}
            disabled={!reachable.length}
          >
            {t("messages.newMessage", "New message")}
          </button>
        }
      />

      {isLocalBackend ? (
        <Banner tone="info">
    {t("messages.runningOnTheLocal", "Running on the local backend, so nothing is actually emailed — messages are recorded so you can see how threads behave. Attach Supabase and set")}
  <code>RESEND_API_KEY</code> to
          send for real.
        </Banner>
      ) : null}

      {!reachable.length && customers.length ? (
        <Banner tone="warn">
          {t("messages.noEmails", "None of your {people} have an email address on", {
            people: tLower("customerPlural", "customers"),
          })}
          file yet, so there is nobody to write to.
        </Banner>
      ) : null}

      {composing ? (
        <Composer
          operator={operator}
          customers={reachable}
          onClose={() => setComposing(false)}
          onSent={(threadId) => { setComposing(false); setActiveId(threadId); invalidate(); }}
        />
      ) : null}

      {!threads.length && !composing ? (
        <EmptyState
          title={t("messages.noConversationsYet", "No conversations yet")}
          message={t("messages.empty.body", "Write to a {customer} and the thread will appear here, replies included.", {
            customer: tLower("customer", "customer"),
          })}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 320px) 1fr", gap: 16, alignItems: "start" }}>
          <ThreadList
            threads={threads}
            customersById={customersById}
            activeId={activeId}
            onSelect={async (id) => {
              setComposing(false);
              setActiveId(id);
              await markThreadRead(id);
              invalidate();
            }}
          />
          {active ? (
            <ThreadView
              thread={active}
              operator={operator}
              customer={customersById[active.customer_id]}
              onSent={invalidate}
            />
          ) : (
            <div className="rms-card rms-card-pad">
              <p style={{ margin: 0, fontSize: 13.5, color: "var(--rms-muted)" }}>
                {t("messages.pickAConversationTo", "Pick a conversation to read it.")}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------

function ThreadList({ threads, customersById, activeId, onSelect }) {
  const { t } = useRms();
  return (
    <div className="rms-card" style={{ overflow: "hidden" }}>
      {threads.map((thread) => {
        const customer = customersById[thread.customer_id];
        const isActive = thread.id === activeId;
        return (
          <button
            key={thread.id}
            type="button"
            onClick={() => onSelect(thread.id)}
            style={{
              display: "block", width: "100%", textAlign: "start",
              padding: "12px 14px", border: 0, cursor: "pointer",
              borderBottom: "1px solid var(--rms-border)",
              background: isActive ? "var(--rms-brand-soft)" : "transparent",
              font: "inherit",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <strong style={{ fontSize: 13.5 }}>
                {customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : t("common.unknown", "Unknown")}
              </strong>
              {thread.unread_count ? (
                <span className="rms-pill rms-pill--info">{thread.unread_count}</span>
              ) : null}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--rms-muted-strong)", marginTop: 2 }}>
              {thread.subject}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--rms-muted)", marginTop: 2 }}>
              {formatDateTime(thread.last_message_at)}
              {thread.status === "closed" ? " · closed" : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------

function ThreadView({ thread, operator, customer, onSent }) {
  const { t } = useRms();
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = React.useCallback(async () => {
    const result = await listMessages(thread.id);
    setMessages(result.data || []);
  }, [thread.id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    setProblem(null);
    adEngine.recordAction();

    const result = await sendMessage({
      operatorId: operator.id,
      customerId: thread.customer_id,
      threadId: thread.id,
      subject: thread.subject,
      body: reply,
    });

    setBusy(false);
    if (!result.success) return setProblem(result.error);

    setReply("");
    await load();
    onSent();
  };

  return (
    <div className="rms-card rms-card-pad">
      <div style={{ borderBottom: "1px solid var(--rms-border)", paddingBottom: 10, marginBottom: 14 }}>
        <h2 className="rms-section-title" style={{ margin: 0 }}>{thread.subject}</h2>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--rms-muted)" }}>
          {customer
            ? `${customer.first_name} ${customer.last_name || ""} · ${customer.email || "no email on file"}`.trim()
            : t("messages.customerGone", "Customer no longer on file")}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 460, overflowY: "auto" }}>
        {messages.map((message) => {
          const outbound = message.direction === "outbound";
          return (
            <div
              key={message.id}
              style={{
                alignSelf: outbound ? "flex-end" : "flex-start",
                maxWidth: "82%",
                background: outbound ? "var(--rms-brand-soft)" : "var(--rms-subtle)",
                border: "1px solid var(--rms-border)",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              {/* whiteSpace preserves the line breaks someone typed. The body is
                  rendered as TEXT — inbound HTML is stored but never injected. */}
              <div style={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>{message.body_text}</div>
              <div style={{ fontSize: 11, color: "var(--rms-muted)", marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                <span>{formatDateTime(message.created_at)}</span>
                {outbound ? (
                  <StatusPill
                    status={DELIVERY_TONE[message.delivery_status] || "pending"}
                    label={message.delivery_status}
                  />
                ) : null}
                {message.template_key ? <span>· automatic</span> : null}
              </div>
              {message.delivery_error && message.delivery_status === "failed" ? (
                <div style={{ fontSize: 11.5, color: "var(--rms-danger)", marginTop: 4 }}>
                  {message.delivery_error}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {problem ? <Banner tone="warn">{problem}</Banner> : null}

      <div style={{ marginTop: 14, borderTop: "1px solid var(--rms-border)", paddingTop: 12 }}>
        <textarea
          className="rms-input"
          rows={4}
          placeholder="Write a reply…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          style={{ resize: "vertical" }}
        />
        <button
          type="button"
          className="rms-btn rms-btn--primary"
          style={{ marginTop: 8 }}
          onClick={send}
          disabled={busy || !reply.trim()}
        >
          {busy ? t("messages.sending", "Sending…") : t("messages.send", "Send")}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function Composer({ operator, customers, onClose, onSent }) {
  const { t } = useRms();
  const [customerId, setCustomerId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const send = async () => {
    setBusy(true);
    setProblem(null);
    adEngine.recordAction();

    const result = await sendMessage({
      operatorId: operator.id,
      customerId,
      subject: subject.trim() || "Your rental",
      body,
    });

    setBusy(false);
    if (!result.success) return setProblem(result.error);
    onSent(result.data?.thread_id);
  };

  return (
    <div className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
      <h2 className="rms-section-title">{t("messages.newMessage", "New message")}</h2>

      <div className="rms-field-grid">
        <Field label="To" required>
          <Select
            value={customerId}
            onChange={setCustomerId}
            options={customers.map((c) => ({
              value: c.id,
              label: `${c.first_name} ${c.last_name || ""} — ${c.email}`.trim(),
            }))}
          />
        </Field>
        <Field label={t("messages.subject", "Subject")}>
          <TextInput value={subject} onChange={setSubject} placeholder={t("messages.yourBookingOnSaturday", "Your booking on Saturday")} />
        </Field>
      </div>

      <textarea
        className="rms-input"
        rows={5}
        placeholder="Write your message…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        style={{ resize: "vertical", marginTop: 8 }}
      />

      {problem ? <Banner tone="warn">{problem}</Banner> : null}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          type="button"
          className="rms-btn rms-btn--primary"
          onClick={send}
          disabled={busy || !customerId || !body.trim()}
        >
          {busy ? t("messages.sending", "Sending…") : t("messages.send", "Send")}
        </button>
        <button type="button" className="rms-btn rms-btn--ghost" onClick={onClose}>
          {t("action.cancel", "Cancel")}
        </button>
      </div>

      <p className="rms-help" style={{ marginTop: 10 }}>
        {t("messages.sentFrom", "Sent from")} <strong>{operator.business_name}</strong> through reservezoo.com. Replies come back
        into this conversation.
      </p>
    </div>
  );
}

export default Messages;
